import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { formatCategory } from '../lib/utils';
import { 
  Package, 
  IndianRupee, 
  TrendingUp, 
  TrendingDown, 
  Warehouse, 
  RefreshCw, 
  Search,
  FileText,
  ShoppingCart,
  Factory,
  Truck,
  Clock,
  AlertTriangle,
  CheckCircle,
  ArrowDownCircle,
  ArrowUpCircle,
  Layers,
  ExternalLink,
  X,
  Filter,
  List,
  Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Alphabet groups for filtering
const ALPHABET_GROUPS = ['All', 'A-E', 'F-J', 'K-O', 'P-T', 'U-Z'];

export default function ItemNettStatusPage() {
  const [items, setItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [nettStatusData, setNettStatusData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  
  // New state for table view
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'detail'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [classificationFilter, setClassificationFilter] = useState('all');
  const [negativeOnly, setNegativeOnly] = useState(false);
  const [alphabetFilter, setAlphabetFilter] = useState('All');
  const [classifications, setClassifications] = useState([]);
  const [tableSearchTerm, setTableSearchTerm] = useState('');
  
  // Detail popup state
  const [detailPopup, setDetailPopup] = useState(null); // { type, title, data }

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  // Fetch all items for dropdown
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/items`, {
          headers: getAuthHeader()
        });
        setItems(response.data);
      } catch (error) {
        toast.error('Failed to load items');
      } finally {
        setItemsLoading(false);
      }
    };
    fetchItems();
  }, []);

  // Fetch classifications
  useEffect(() => {
    const fetchClassifications = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/items/classifications`, {
          headers: getAuthHeader()
        });
        setClassifications(response.data.classifications || []);
      } catch (error) {
        console.error('Failed to load classifications:', error);
      }
    };
    fetchClassifications();
  }, []);

  // Fetch nett status when item is selected
  const fetchNettStatus = async (itemId) => {
    if (!itemId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/items/${itemId}/nett-status`, {
        headers: getAuthHeader()
      });
      setNettStatusData(response.data);
    } catch (error) {
      toast.error('Failed to load item status');
      console.error(error);
      setViewMode('table');
      setSelectedItemId('');
    } finally {
      setLoading(false);
    }
  };

  const handleItemSelect = (itemId) => {
    setSelectedItemId(itemId);
    setViewMode('detail');
    fetchNettStatus(itemId);
  };

  // Helper to check if item name falls in alphabet group
  const isInAlphabetGroup = (name, group) => {
    if (group === 'All') return true;
    const firstChar = (name || '').charAt(0).toUpperCase();
    switch (group) {
      case 'A-E': return 'ABCDE'.includes(firstChar);
      case 'F-J': return 'FGHIJ'.includes(firstChar);
      case 'K-O': return 'KLMNO'.includes(firstChar);
      case 'P-T': return 'PQRST'.includes(firstChar);
      case 'U-Z': return 'UVWXYZ'.includes(firstChar);
      default: return true;
    }
  };

  // Filtered items for table view
  const tableFilteredItems = useMemo(() => {
    let filtered = [...items];

    // CTM Visibility (Feb 2026) — CTM items show here for purchase planning. The
    // table row uses bin_card_stock as its stock value; numeric Ledger 1 fields
    // (wip/ohwip/blocked/so_blocked) are 0 for CTM by construction.

    // Filter by category
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => 
        item.category?.toUpperCase() === categoryFilter.toUpperCase()
      );
    }
    
    // Filter by classification
    if (classificationFilter !== 'all') {
      filtered = filtered.filter(item => 
        item.classification?.toUpperCase() === classificationFilter.toUpperCase()
      );
    }
    
    // Filter by negative nett status
    // Use item.nett_status directly from backend (no frontend calculation)
    if (negativeOnly) {
      filtered = filtered.filter(item => (item.nett_status || 0) < 0);
    }
    
    // Filter by alphabet group
    filtered = filtered.filter(item => isInAlphabetGroup(item.name, alphabetFilter));
    
    // Filter by search term
    if (tableSearchTerm.trim()) {
      const term = tableSearchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.name?.toLowerCase().includes(term) ||
        item.item_code?.toLowerCase().includes(term)
      );
    }
    
    // Sort alphabetically
    filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    
    return filtered;
  }, [items, categoryFilter, classificationFilter, negativeOnly, alphabetFilter, tableSearchTerm]);

  // Filter items for dropdown based on search
  const filteredItems = useMemo(() => {
    if (!searchTerm) return items;
    const term = searchTerm.toLowerCase();
    return items.filter(item => 
      item.name?.toLowerCase().includes(term) ||
      item.description?.toLowerCase().includes(term)
    );
  }, [items, searchTerm]);

  // REMOVED: calculateNettStatus function
  // Rule: Frontend NEVER calculates nett status. Use item.nett_status from backend.
  // For live nett status, call the nett-status endpoint and read stock_position.current.nett_status

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const data = nettStatusData;
  const stock = data?.stock_position;

  // Fix 7 — the backend `stock_position.current.stock_in_hand` is now the
  // authoritative Ledger-1 computed SIH (opening + Ledger-1 incoming − Ledger-1 outgoing,
  // with bin_card_issue / ctm_issue / ctm_return filtered out). Use it directly.
  // Part D Legacy SIH row reads the parallel `stock_in_hand_cached` field so users can
  // still see the pre-rewrite cached value and the drift magnitude.
  const displaySih = stock?.current?.stock_in_hand ?? 0;
  const cachedSih = stock?.current?.stock_in_hand_cached ?? displaySih;
  const sihDelta = (cachedSih || 0) - (displaySih || 0);
  const sihDrift = Math.abs(sihDelta) > 0.01;
  const LEGACY_SIH_TOOLTIP = "This is the historical cached value. It may include untracked stock changes from before the ledger was complete. Will be reconciled at the upcoming rebuild.";

  // Clear detail view and go back to table
  const backToTable = () => {
    setViewMode('table');
    setSelectedItemId('');
    setNettStatusData(null);
  };

  return (
    <DashboardLayout>
      <div className="space-y-3">
        {/* Table View Mode */}
        {viewMode === 'table' && (
          <>
            {/* Compact Header with Filters */}
            <Card>
              <CardContent className="py-3">
                <div className="space-y-3">
                  {/* Title + Filter Row Combined */}
                  <div className="flex flex-wrap gap-3 items-center">
                    {/* Title */}
                    <div className="flex items-center gap-2 mr-4">
                      <Package className="w-5 h-5 text-primary" />
                      <h1 className="text-lg font-bold">Item Nett Status</h1>
                    </div>
                    
                    {/* Search */}
                    <div className="flex-1 min-w-[180px] max-w-[300px]">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search by name or code..."
                          value={tableSearchTerm}
                          onChange={(e) => setTableSearchTerm(e.target.value)}
                          className="pl-8 h-8 text-sm"
                          data-testid="table-search"
                        />
                      </div>
                    </div>
                    
                    {/* Category Filter */}
                    <div className="w-[120px]">
                      <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                        <SelectTrigger data-testid="category-filter" className="h-8 text-sm">
                          <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Categories</SelectItem>
                          <SelectItem value="RMT">RMT</SelectItem>
                          <SelectItem value="SFG">SFG</SelectItem>
                          <SelectItem value="FNG">FNG</SelectItem>
                          <SelectItem value="CTM">CTM</SelectItem>
                          <SelectItem value="ASSET">ASSET</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Classification Filter */}
                    <div className="w-[140px]">
                      <Select value={classificationFilter} onValueChange={setClassificationFilter}>
                        <SelectTrigger data-testid="classification-filter" className="h-8 text-sm">
                          <SelectValue placeholder="Classification" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Classifications</SelectItem>
                          {classifications.sort().map(c => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Negative Only Toggle */}
                    <div className="flex items-center gap-1.5">
                      <Checkbox 
                        id="negative-only" 
                        checked={negativeOnly}
                        onCheckedChange={setNegativeOnly}
                        data-testid="negative-only-filter"
                        className="h-4 w-4"
                      />
                      <Label htmlFor="negative-only" className="text-xs cursor-pointer flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 text-red-500" />
                        Negative
                      </Label>
                    </div>
                  </div>
                  
                  {/* Alphabet Filter Tabs */}
                  <div className="flex gap-0.5 flex-wrap">
                    {ALPHABET_GROUPS.map(group => (
                      <Button
                        key={group}
                        variant={alphabetFilter === group ? "default" : "outline"}
                        size="sm"
                        onClick={() => setAlphabetFilter(group)}
                        className="min-w-[40px] h-7 text-xs px-2"
                        data-testid={`alphabet-filter-${group}`}
                      >
                        {group}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Results Summary - More compact */}
            <div className="flex justify-between items-center px-1">
              <p className="text-xs text-muted-foreground">
                {tableFilteredItems.length} of {items.length} items
                {negativeOnly && <span className="text-red-500 ml-1">(Negative only)</span>}
              </p>
              <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => {
                setCategoryFilter('all');
                setClassificationFilter('all');
                setNegativeOnly(false);
                setAlphabetFilter('All');
                setTableSearchTerm('');
              }}>
                <X className="w-3 h-3 mr-1" />
                Clear
              </Button>
            </div>
            
            {/* Table View */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-auto max-h-[calc(100vh-220px)]">
                  {/* Sticky Header - Compact */}
                  <div className="sticky top-0 z-10 bg-slate-100 border-b">
                    <div className="grid grid-cols-8 gap-1 px-3 py-2 text-[11px] font-semibold text-slate-600">
                      <div className="text-center">Stock</div>
                      <div className="text-center">WIP</div>
                      <div className="text-center">OHWIP</div>
                      <div className="text-center">Indent</div>
                      <div className="text-center">PO</div>
                      <div className="text-center">Blocked</div>
                      <div className="text-center">SO Blk</div>
                      <div className="text-center">Nett</div>
                    </div>
                  </div>
                  
                  {/* Items */}
                  {itemsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-5 h-5 animate-spin text-primary" />
                      <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
                    </div>
                  ) : tableFilteredItems.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Package className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">No items match your filters</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {tableFilteredItems.map(item => {
                        // Use item.nett_status directly from backend (no frontend calculation)
                        const nettStatus = item.nett_status || 0;
                        const isNegative = nettStatus < 0;
                        
                        return (
                          <div 
                            key={item.id} 
                            className="hover:bg-slate-50 cursor-pointer transition-colors py-1.5 px-3"
                            onClick={() => handleItemSelect(item.id)}
                            data-testid={`item-row-${item.id}`}
                          >
                            {/* Row 1: Item Name - Compact */}
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <span className="font-medium text-sm text-slate-800 truncate">{item.name}</span>
                                <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0">
                                  {formatCategory(item.category)}
                                </Badge>
                              </div>
                              {item.classification && (
                                <Badge variant="secondary" className="shrink-0 text-[10px] px-1.5 py-0">
                                  {item.classification}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Row 2: Values - Reordered: Indent before PO */}
                            <div className="grid grid-cols-8 gap-1 text-xs">
                              <div className="text-center font-medium">
                                {(item.stock_in_hand || 0).toFixed(2)}
                              </div>
                              <div className="text-center text-purple-600">
                                {(item.wip || 0).toFixed(2)}
                              </div>
                              <div className="text-center text-yellow-600">
                                {(item.ohwip || 0).toFixed(2)}
                              </div>
                              <div className="text-center text-teal-600">
                                {(item.indent_qty || 0).toFixed(2)}
                              </div>
                              <div className="text-center text-blue-600">
                                {(item.po_issued || 0).toFixed(2)}
                              </div>
                              <div className="text-center text-orange-600">
                                {(item.blocked || 0).toFixed(2)}
                              </div>
                              <div className="text-center text-red-600">
                                {(item.so_blocked || 0).toFixed(2)}
                              </div>
                              <div className={`text-center font-bold flex items-center justify-center gap-0.5 ${isNegative ? 'text-red-600' : 'text-green-600'}`}>
                                {isNegative && <AlertTriangle className="w-3 h-3" />}
                                {nettStatus.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Detail View Mode - Individual Item Search */}
        {viewMode === 'detail' && (
          <>
            {/* Compact Header for Detail View */}
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-primary" />
                <h1 className="text-lg font-bold">Item Nett Status</h1>
                <span className="text-sm text-muted-foreground">- Detail View</span>
              </div>
              <Button variant="outline" size="sm" onClick={backToTable}>
                <List className="w-4 h-4 mr-1" />
                Back to List
              </Button>
            </div>
            
            {/* Item Selector */}
            <Card>
              <CardContent className="py-3">
                <div className="flex flex-col sm:flex-row gap-3 items-end">
                  <div className="flex-1 min-w-0">
                    <label className="text-sm font-medium mb-1 block">Select Item</label>
                    <Popover open={open} onOpenChange={setOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={open}
                          className="w-full justify-between h-9"
                          data-testid="item-selector"
                        >
                          <span className="truncate max-w-[calc(100%-24px)]">
                            {selectedItemId
                              ? items.find(item => item.id === selectedItemId)?.name || "Select item..."
                              : "Select item..."}
                          </span>
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command shouldFilter={false}>
                          <CommandInput 
                            placeholder="Search items..." 
                            value={searchTerm}
                            onValueChange={setSearchTerm}
                          />
                          <CommandList>
                            <CommandEmpty>No item found.</CommandEmpty>
                            <CommandGroup>
                              {filteredItems.map(item => (
                                <CommandItem
                                  key={item.id}
                                  value={item.id}
                                  onSelect={() => {
                                    handleItemSelect(item.id);
                                    setOpen(false);
                                    setSearchTerm('');
                                  }}
                                >
                                  <span className="font-medium truncate">{item.name}</span>
                                  <Badge variant="outline" className="ml-2 text-xs shrink-0">{formatCategory(item.category)}</Badge>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  {selectedItemId && (
                    <Button 
                      variant="outline" 
                      onClick={() => fetchNettStatus(selectedItemId)}
                      disabled={loading}
                      className="shrink-0"
                    >
                      <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Loading State */}
            {loading && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">Loading item status...</span>
          </div>
        )}

        {/* No Item Selected */}
        {!selectedItemId && !loading && (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Search className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">Select an item from the dropdown to view its complete status</p>
            </CardContent>
          </Card>
        )}

        {/* Item Data Display */}
        {data && !loading && data.is_ctm && (
          <>
            {/* CTM Visibility (Feb 2026) — slim view for purchase planning.
                CTM items have no Ledger 1 stock, no WO/SO blocking, no JC flow.
                Show only: Bin Card Stock, Opening Bin Card, On Indent, On PO. */}
            <Card className="bg-gradient-to-r from-amber-100/40 to-amber-50/40">
              <CardContent className="pt-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h2 className="text-xl font-bold" data-testid="ctm-item-code">{data.item.code}</h2>
                    <p className="text-muted-foreground">{data.item.name}</p>
                    <div className="flex gap-2 mt-2">
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200">{formatCategory(data.item.category)}</Badge>
                      <Badge variant="outline">{data.item.unit}</Badge>
                      <Badge variant="outline" className="text-xs">Ledger 2 (Bin Card only)</Badge>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Tracked on Bin Card</p>
                    <p className="text-xs text-muted-foreground">No Ledger 1 valuation / WIP / blocking</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Four cards: Bin Card Stock | Opening Bin Card | On Indent | On PO */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card data-testid="ctm-card-bin-card-stock">
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-muted-foreground">Bin Card Stock</p>
                      <p className="text-2xl font-bold text-blue-600">{(data.bin_card?.bin_card_stock || 0).toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">Physical floor qty</p>
                    </div>
                    <Warehouse className="w-8 h-8 text-blue-200 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="ctm-card-opening-bin-card">
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-muted-foreground">Opening Bin Card</p>
                      <p className="text-2xl font-bold text-slate-700">{(data.bin_card?.opening_bin_card_stock || 0).toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">Initial bin card</p>
                    </div>
                    <Package className="w-8 h-8 text-slate-200 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="ctm-card-on-indent">
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-muted-foreground">On Indent</p>
                      <p className="text-2xl font-bold text-teal-600">{(data.bin_card?.indent_qty || 0).toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">{data.counts?.indent_pending_count || 0} pending</p>
                    </div>
                    <Clock className="w-8 h-8 text-teal-200 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="ctm-card-on-po">
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-muted-foreground">On PO</p>
                      <p className="text-2xl font-bold text-purple-600">{(data.bin_card?.po_issued || 0).toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">{data.counts?.po_pending_count || 0} open</p>
                    </div>
                    <ShoppingCart className="w-8 h-8 text-purple-200 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Pending Indents table */}
            {(data.indent_pending || []).length > 0 && (
              <Card data-testid="ctm-indent-pending-list">
                <CardHeader className="py-3">
                  <CardTitle className="text-base">Pending Indents ({data.indent_pending.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-2">Indent #</th>
                          <th className="text-left p-2">Status</th>
                          <th className="text-right p-2">Requested</th>
                          <th className="text-right p-2">Ordered</th>
                          <th className="text-right p-2">Remaining</th>
                          <th className="text-left p-2">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.indent_pending.map((ind, i) => (
                          <tr key={ind.indent_id || i} className="border-t">
                            <td className="p-2 font-medium">{ind.indent_number}</td>
                            <td className="p-2"><Badge variant="outline" className="text-xs">{ind.status}</Badge></td>
                            <td className="p-2 text-right">{(ind.requested_qty || 0).toFixed(2)}</td>
                            <td className="p-2 text-right">{(ind.issued_qty || 0).toFixed(2)}</td>
                            <td className="p-2 text-right font-medium">{(ind.remaining_to_order || 0).toFixed(2)}</td>
                            <td className="p-2 text-muted-foreground">{formatDate(ind.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Open POs table */}
            {(data.po_pending || []).length > 0 && (
              <Card data-testid="ctm-po-pending-list">
                <CardHeader className="py-3">
                  <CardTitle className="text-base">Open Purchase Orders ({data.po_pending.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-2">PO #</th>
                          <th className="text-left p-2">Supplier</th>
                          <th className="text-left p-2">Status</th>
                          <th className="text-right p-2">Ordered</th>
                          <th className="text-right p-2">Received</th>
                          <th className="text-right p-2">Pending</th>
                          <th className="text-left p-2">Expected</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.po_pending.map((po, i) => (
                          <tr key={po.po_id || i} className="border-t">
                            <td className="p-2 font-medium">{po.po_number}</td>
                            <td className="p-2">{po.supplier_name || '-'}</td>
                            <td className="p-2"><Badge variant="outline" className="text-xs">{po.status}</Badge></td>
                            <td className="p-2 text-right">{(po.ordered_qty || 0).toFixed(2)}</td>
                            <td className="p-2 text-right">{(po.received_qty || 0).toFixed(2)}</td>
                            <td className="p-2 text-right font-medium">{(po.pending_qty || 0).toFixed(2)}</td>
                            <td className="p-2 text-muted-foreground">{formatDate(po.expected_date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {(data.indent_pending || []).length === 0 && (data.po_pending || []).length === 0 && (
              <Card className="border-dashed">
                <CardContent className="py-6 text-center text-sm text-muted-foreground" data-testid="ctm-no-pending">
                  No pending indents or open POs for this CTM item.
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Item Data Display */}
        {data && !loading && !data.is_ctm && (
          <>
            {/* Item Info Header */}
            <Card className="bg-gradient-to-r from-primary/10 to-primary/5">
              <CardContent className="pt-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h2 className="text-xl font-bold">{data.item.code}</h2>
                    <p className="text-muted-foreground">{data.item.name}</p>
                    <div className="flex gap-2 mt-2">
                      <Badge>{formatCategory(data.item.category)}</Badge>
                      <Badge variant="outline">{data.item.unit}</Badge>
                    </div>
                  </div>
                  <div className="text-right space-y-1">
                    <div>
                      <p className="text-sm text-muted-foreground">Nett Status (Procurement)</p>
                      <p className="text-2xl font-bold text-blue-600" data-testid="header-nett-status">{(stock?.current?.nett_status ?? 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Nett Available (Issuance)</p>
                      <p className="text-xl font-bold text-primary" data-testid="header-nett-available">{(stock?.current?.nett_available ?? 0).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stock Position Cards - Clickable for details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-8 gap-4">
              {/* Stock in Hand - Clickable */}
              <Card 
                className="cursor-pointer hover:shadow-md hover:border-blue-300 transition-all"
                onClick={() => setDetailPopup({
                  type: 'stock_in_hand',
                  title: 'Stock in Hand Details',
                  description: 'Breakdown of available stock from purchases and production'
                })}
                data-testid="card-stock-in-hand"
              >
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        Stock in Hand <ExternalLink className="w-3 h-3" />
                      </p>
                      <p className="text-2xl font-bold" data-testid="stock-in-hand-headline">{(displaySih || 0).toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">{formatCurrency(stock?.current?.stock_value)}</p>
                      {sihDrift && (
                        <div className="mt-2 pt-2 border-t border-dashed border-muted" data-testid="sih-legacy-block">
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <span>Legacy SIH (pre-rewrite)</span>
                            <span
                              title={LEGACY_SIH_TOOLTIP}
                              className="cursor-help inline-flex"
                              data-testid="sih-legacy-tooltip"
                            >
                              <Info className="w-3 h-3" />
                            </span>
                          </div>
                          <p className="text-sm font-medium text-muted-foreground" data-testid="sih-legacy-value">{cachedSih.toFixed(2)}</p>
                          <p
                            className={`text-xs font-medium ${sihDelta > 0 ? 'text-red-600' : 'text-amber-600'}`}
                            data-testid="sih-legacy-delta"
                          >
                            Delta: {sihDelta > 0 ? '+' : ''}{sihDelta.toFixed(2)}
                          </p>
                        </div>
                      )}
                    </div>
                    <Warehouse className="w-8 h-8 text-blue-200 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>

              {/* OHWIP - Clickable (Items to be produced in WO) */}
              <Card 
                className="cursor-pointer hover:shadow-md hover:border-yellow-300 transition-all"
                onClick={() => setDetailPopup({
                  type: 'ohwip',
                  title: 'OHWIP (On-Hold WIP)',
                  description: 'Items to be produced in Work Orders - awaiting JC issue'
                })}
                data-testid="card-ohwip"
              >
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        OHWIP (Comp) <ExternalLink className="w-3 h-3" />
                      </p>
                      <p className="text-2xl font-bold text-yellow-600">{(stock?.current?.ohwip || 0).toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">To be Produced</p>
                    </div>
                    <Clock className="w-8 h-8 text-yellow-200" />
                  </div>
                </CardContent>
              </Card>

              {/* WIP - Clickable */}
              <Card 
                className="cursor-pointer hover:shadow-md hover:border-purple-300 transition-all"
                onClick={() => setDetailPopup({
                  type: 'wip',
                  title: 'WIP (Work in Progress)',
                  description: 'Items currently being produced in active Job Cards'
                })}
                data-testid="card-wip"
              >
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        WIP (Comp) <ExternalLink className="w-3 h-3" />
                      </p>
                      <p className="text-2xl font-bold text-purple-600">{(stock?.current?.wip || 0).toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">Being Produced</p>
                    </div>
                    <Factory className="w-8 h-8 text-purple-200" />
                  </div>
                </CardContent>
              </Card>

              {/* WIP RMT - Clickable */}
              <Card 
                className="cursor-pointer hover:shadow-md hover:border-orange-300 transition-all"
                onClick={() => setDetailPopup({
                  type: 'wip_rmt',
                  title: 'WIP (Raw Materials)',
                  description: 'Raw materials issued to production - in active Job Cards'
                })}
                data-testid="card-wip-rmt"
              >
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        WIP (RMT) <ExternalLink className="w-3 h-3" />
                      </p>
                      <p className="text-2xl font-bold text-orange-600">{(stock?.current?.wip_rmt || 0).toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">RMT in Production</p>
                    </div>
                    <Package className="w-8 h-8 text-orange-200" />
                  </div>
                </CardContent>
              </Card>

              {/* Indent Qty - Clickable (BEFORE PO Issued) */}
              <Card 
                className="cursor-pointer hover:shadow-md hover:border-teal-300 transition-all"
                onClick={() => setDetailPopup({
                  type: 'indent_qty',
                  title: 'Indent Quantity',
                  description: 'Purchase requested via Indents - awaiting PO approval'
                })}
                data-testid="card-indent-qty"
              >
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        Indent Qty <ExternalLink className="w-3 h-3" />
                      </p>
                      <p className="text-2xl font-bold text-teal-600">{(stock?.current?.indent_qty || 0).toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">Purchase Requested</p>
                    </div>
                    <FileText className="w-8 h-8 text-teal-200" />
                  </div>
                </CardContent>
              </Card>

              {/* PO Issued - Clickable */}
              <Card 
                className="cursor-pointer hover:shadow-md hover:border-green-300 transition-all"
                onClick={() => setDetailPopup({
                  type: 'po_issued',
                  title: 'PO Issued',
                  description: 'Pending Purchase Orders for this item'
                })}
                data-testid="card-po-issued"
              >
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        PO Issued <ExternalLink className="w-3 h-3" />
                      </p>
                      <p className="text-2xl font-bold text-green-600">{(stock?.current?.po_issued || 0).toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">On Order</p>
                    </div>
                    <Truck className="w-8 h-8 text-green-200" />
                  </div>
                </CardContent>
              </Card>

              {/* Blocked - Clickable */}
              <Card 
                className="cursor-pointer hover:shadow-md hover:border-orange-300 transition-all"
                onClick={() => setDetailPopup({
                  type: 'blocked',
                  title: 'Blocked for Work Orders',
                  description: 'Quantity reserved for pending Work Orders'
                })}
                data-testid="card-blocked"
              >
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        Blocked (WO) <ExternalLink className="w-3 h-3" />
                      </p>
                      <p className="text-2xl font-bold text-orange-600">{(stock?.current?.blocked || 0).toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">For Work Orders</p>
                    </div>
                    <AlertTriangle className="w-8 h-8 text-orange-200" />
                  </div>
                </CardContent>
              </Card>

              {/* SO Blocked - Clickable */}
              <Card 
                className="cursor-pointer hover:shadow-md hover:border-red-300 transition-all"
                onClick={() => setDetailPopup({
                  type: 'so_blocked',
                  title: 'SO Blocked',
                  description: 'Quantity reserved for Sales Orders (Buy type)'
                })}
                data-testid="card-so-blocked"
              >
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        SO Blocked <ExternalLink className="w-3 h-3" />
                      </p>
                      <p className="text-2xl font-bold text-red-600">{(stock?.current?.so_blocked || 0).toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">For Sales Orders</p>
                    </div>
                    <ShoppingCart className="w-8 h-8 text-red-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Stock Flow Summary */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ArrowDownCircle className="w-4 h-4 text-green-600" />
                    Total Incoming
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Opening Stock:</span>
                      <span className="font-medium">{stock?.opening_stock?.quantity || 0}</span>
                    </div>
                    <div 
                      className="flex justify-between cursor-pointer hover:bg-green-50 rounded px-1 -mx-1"
                      onClick={() => (stock?.incoming?.purchase?.quantity || 0) > 0 && setDetailPopup({
                        type: 'purchase',
                        title: 'Purchase Entries',
                        description: 'Items received through Purchase Entries'
                      })}
                      data-testid="row-purchase"
                    >
                      <span className={`flex items-center gap-1 ${(stock?.incoming?.purchase?.quantity || 0) > 0 ? 'text-green-700 hover:underline' : ''}`}>
                        Purchase {(stock?.incoming?.purchase?.quantity || 0) > 0 && <ExternalLink className="w-3 h-3" />}
                      </span>
                      <span className="font-medium">{stock?.incoming?.purchase?.quantity || 0}</span>
                    </div>
                    <div 
                      className="flex justify-between cursor-pointer hover:bg-green-50 rounded px-1 -mx-1"
                      onClick={() => (stock?.incoming?.production?.quantity || 0) > 0 && setDetailPopup({
                        type: 'production',
                        title: 'Production Entries',
                        description: 'Items received from Work Order production'
                      })}
                      data-testid="row-production"
                    >
                      <span className={`flex items-center gap-1 ${(stock?.incoming?.production?.quantity || 0) > 0 ? 'text-green-700 hover:underline' : ''}`}>
                        Production {(stock?.incoming?.production?.quantity || 0) > 0 && <ExternalLink className="w-3 h-3" />}
                      </span>
                      <span className="font-medium">{stock?.incoming?.production?.quantity || 0}</span>
                    </div>
                    <div 
                      className="flex justify-between cursor-pointer hover:bg-green-50 rounded px-1 -mx-1"
                      onClick={() => (stock?.incoming?.bpd?.quantity || 0) > 0 && setDetailPopup({
                        type: 'bpd',
                        title: 'BPD Entries',
                        description: 'Items received through Buyer Product Dispatch'
                      })}
                      data-testid="row-bpd"
                    >
                      <span className={`flex items-center gap-1 ${(stock?.incoming?.bpd?.quantity || 0) > 0 ? 'text-green-700 hover:underline' : ''}`}>
                        BPD {(stock?.incoming?.bpd?.quantity || 0) > 0 && <ExternalLink className="w-3 h-3" />}
                      </span>
                      <span className="font-medium">{stock?.incoming?.bpd?.quantity || 0}</span>
                    </div>
                    <div 
                      className="flex justify-between cursor-pointer hover:bg-green-50 rounded px-1 -mx-1"
                      onClick={() => (stock?.incoming?.dc_inward?.quantity || 0) > 0 && setDetailPopup({
                        type: 'dc_inward',
                        title: 'DC Inward Entries',
                        description: 'Items received through Delivery Challans'
                      })}
                      data-testid="row-dc-inward"
                    >
                      <span className={`flex items-center gap-1 ${(stock?.incoming?.dc_inward?.quantity || 0) > 0 ? 'text-green-700 hover:underline' : ''}`}>
                        DC Inward {(stock?.incoming?.dc_inward?.quantity || 0) > 0 && <ExternalLink className="w-3 h-3" />}
                      </span>
                      <span className="font-medium">{(stock?.incoming?.dc_inward?.quantity || 0).toFixed(2)}</span>
                    </div>
                    <div 
                      className="flex justify-between cursor-pointer hover:bg-green-50 rounded px-1 -mx-1"
                      onClick={() => (stock?.incoming?.stock_corrections?.quantity || 0) !== 0 && setDetailPopup({
                        type: 'stock_corrections',
                        title: 'Stock Corrections',
                        description: 'Positive movements not backed by a batch (system reconciliations, reversals, adjustments)'
                      })}
                      data-testid="row-stock-corrections"
                    >
                      <span className={`flex items-center gap-1 ${(stock?.incoming?.stock_corrections?.quantity || 0) !== 0 ? 'text-green-700 hover:underline' : ''}`}>
                        Stock Corrections {(stock?.incoming?.stock_corrections?.quantity || 0) !== 0 && <ExternalLink className="w-3 h-3" />}
                      </span>
                      <span className="font-medium">{(stock?.incoming?.stock_corrections?.quantity || 0).toFixed(2)}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between font-bold">
                      <span>Total:</span>
                      <span className="text-green-600">{(stock?.incoming?.total?.quantity || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Value:</span>
                      <span>{formatCurrency(stock?.incoming?.total?.value)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ArrowUpCircle className="w-4 h-4 text-red-600" />
                    Total Outgoing
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div 
                      className="flex justify-between cursor-pointer hover:bg-red-50 rounded px-1 -mx-1"
                      onClick={() => (stock?.outgoing?.jc_issued?.quantity || 0) > 0 && setDetailPopup({
                        type: 'jc_issued',
                        title: 'JC Issues',
                        description: 'Items issued to Job Cards'
                      })}
                      data-testid="row-jc-issued"
                    >
                      <span className={`flex items-center gap-1 ${(stock?.outgoing?.jc_issued?.quantity || 0) > 0 ? 'text-red-700 hover:underline' : ''}`}>
                        JC Issued {(stock?.outgoing?.jc_issued?.quantity || 0) > 0 && <ExternalLink className="w-3 h-3" />}
                      </span>
                      <span className="font-medium">{stock?.outgoing?.jc_issued?.quantity || 0}</span>
                    </div>
                    <div 
                      className="flex justify-between cursor-pointer hover:bg-red-50 rounded px-1 -mx-1"
                      onClick={() => (stock?.outgoing?.sales?.quantity || 0) > 0 && setDetailPopup({
                        type: 'sales',
                        title: 'Sales/Dispatch',
                        description: 'Items dispatched for sales'
                      })}
                      data-testid="row-sales"
                    >
                      <span className={`flex items-center gap-1 ${(stock?.outgoing?.sales?.quantity || 0) > 0 ? 'text-red-700 hover:underline' : ''}`}>
                        Sales {(stock?.outgoing?.sales?.quantity || 0) > 0 && <ExternalLink className="w-3 h-3" />}
                      </span>
                      <span className="font-medium">{stock?.outgoing?.sales?.quantity || 0}</span>
                    </div>
                    <div 
                      className="flex justify-between cursor-pointer hover:bg-red-50 rounded px-1 -mx-1"
                      onClick={() => (stock?.outgoing?.dc_outward?.quantity || 0) > 0 && setDetailPopup({
                        type: 'dc_outward',
                        title: 'DC Outward',
                        description: 'Items sent via Delivery Challans'
                      })}
                      data-testid="row-dc-outward"
                    >
                      <span className={`flex items-center gap-1 ${(stock?.outgoing?.dc_outward?.quantity || 0) > 0 ? 'text-red-700 hover:underline' : ''}`}>
                        DC Outward {(stock?.outgoing?.dc_outward?.quantity || 0) > 0 && <ExternalLink className="w-3 h-3" />}
                      </span>
                      <span className="font-medium">{stock?.outgoing?.dc_outward?.quantity || 0}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between font-bold">
                      <span>Total:</span>
                      <span className="text-red-600">{stock?.outgoing?.total?.quantity || 0}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Value:</span>
                      <span>{formatCurrency(stock?.outgoing?.total?.value)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-50 to-purple-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <IndianRupee className="w-4 h-4 text-primary" />
                    Current Position
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Stock in Hand:</span>
                      <span className="font-medium">{(displaySih || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>(+) WIP (Comp):</span>
                      <span className="font-medium text-purple-600">+{(stock?.current?.wip || 0).toFixed(2)}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between font-bold">
                      <span>Nett Available:</span>
                      <span className="text-primary">{(stock?.current?.nett_available || 0).toFixed(2)}</span>
                    </div>
                    <div className="text-xs text-muted-foreground italic">
                      = Stock + WIP (Comp)
                    </div>
                    <div className="flex justify-between mt-1">
                      <span>Free Stock:</span>
                      <span className="font-medium">{(stock?.current?.free_stock || 0).toFixed(2)}</span>
                    </div>
                    <div className="text-xs text-muted-foreground italic">
                      = Stock - Blocked
                    </div>
                    <div className="border-t pt-2 mt-2">
                      <div className="flex justify-between">
                        <span>(+) OHWIP (Comp):</span>
                        <span className="font-medium text-yellow-600">+{(stock?.current?.ohwip || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>(+) Indent Qty:</span>
                        <span className="font-medium text-teal-600">+{(stock?.current?.indent_qty || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>(+) PO Issued:</span>
                        <span className="font-medium text-green-600">+{(stock?.current?.po_issued || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>(-) Blocked:</span>
                        <span className="font-medium text-orange-600">-{(stock?.current?.blocked || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>(-) SO Blocked:</span>
                        <span className="font-medium text-red-600">-{(stock?.current?.so_blocked || 0).toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="border-t pt-2 flex justify-between font-bold text-lg">
                      <span>Nett Status:</span>
                      <span className="text-blue-600">{(stock?.current?.nett_status || 0).toFixed(2)}</span>
                    </div>
                    <div className="text-xs text-muted-foreground italic">
                      = Stock + OHWIP + WIP + PO + Indent - Blocked - SO_Blocked
                    </div>
                    <div className="border-t pt-2 mt-2 flex justify-between text-muted-foreground">
                      <span>Avg Price:</span>
                      <span>{formatCurrency(stock?.current?.avg_price)}</span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span>Stock Value:</span>
                      <span>{formatCurrency(stock?.current?.stock_value)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Transaction Tabs */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Transaction History</CardTitle>
                <CardDescription>
                  Indents: {data.counts?.indent_pending_count} pending
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="jc_ojc">
                  <TabsList className="grid grid-cols-4 w-full">
                    <TabsTrigger value="purchases" data-testid="tab-purchases">
                      Purchases
                    </TabsTrigger>
                    <TabsTrigger value="sales" data-testid="tab-sales">
                      Sales
                    </TabsTrigger>
                    <TabsTrigger value="jc_ojc" data-testid="tab-jc-ojc">
                      JC/OJC Issues
                    </TabsTrigger>
                    <TabsTrigger value="reserved_stock" data-testid="tab-reserved-stock">
                      Reserved Stock ({nettStatusData?.blocked_details?.length || 0})
                    </TabsTrigger>
                  </TabsList>

                  {/* Purchases Tab */}
                  <TabsContent value="purchases">
                    {data.purchase_entries?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead>Reference</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead className="text-right">Qty</TableHead>
                            <TableHead className="text-right">Unit Price</TableHead>
                            <TableHead className="text-right">Value</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.purchase_entries.map((entry, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{entry.reference}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{entry.reference_type}</Badge>
                              </TableCell>
                              <TableCell className="text-right text-green-600">+{entry.quantity || 0}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.unit_price)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.value)}</TableCell>
                              <TableCell>{formatDate(entry.date)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">No purchase entries found</p>
                    )}
                  </TabsContent>

                  {/* Sales Tab */}
                  <TabsContent value="sales">
                    {data.sales_entries?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead>Reference</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead className="text-right">Qty</TableHead>
                            <TableHead className="text-right">Unit Price</TableHead>
                            <TableHead className="text-right">Value</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.sales_entries.map((entry, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{entry.reference}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{entry.reference_type}</Badge>
                              </TableCell>
                              <TableCell className="text-right text-red-600">-{entry.quantity || 0}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.unit_price)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.value)}</TableCell>
                              <TableCell>{formatDate(entry.date)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">No sales entries found</p>
                    )}
                  </TabsContent>

                  {/* JC/OJC Tab */}
                  <TabsContent value="jc_ojc">
                    {data.jc_ojc_entries?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead>Type</TableHead>
                            <TableHead>Reference</TableHead>
                            <TableHead className="text-right">Qty</TableHead>
                            <TableHead className="text-right">Unit Price</TableHead>
                            <TableHead className="text-right">Value</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.jc_ojc_entries.map((entry, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                <Badge variant={entry.type === 'JC' ? 'default' : 'secondary'}>
                                  {entry.type}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-medium">{entry.reference}</TableCell>
                              <TableCell className="text-right text-red-600">-{entry.quantity || 0}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.unit_price)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.value)}</TableCell>
                              <TableCell>{formatDate(entry.date)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">No JC/OJC issue entries found</p>
                    )}
                  </TabsContent>

                  {/* Reserved Stock Tab */}
                  <TabsContent value="reserved_stock">
                    {nettStatusData?.blocked_details?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead>WO Number</TableHead>
                            <TableHead>FG Item</TableHead>
                            <TableHead>SO</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Required</TableHead>
                            <TableHead className="text-right">Issued</TableHead>
                            <TableHead className="text-right">Pending</TableHead>
                            <TableHead>Delivery</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nettStatusData.blocked_details.map((res, idx) => (
                            <TableRow key={idx} data-testid={`blocked-row-${idx}`}>
                              <TableCell className="font-medium font-mono">{res.work_order_number || '-'}</TableCell>
                              <TableCell className="text-sm text-muted-foreground truncate max-w-[200px]">{res.parent_item_name || '-'}</TableCell>
                              <TableCell className="font-mono text-sm">{res.so_number || '-'}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{res.wo_status || '-'}</Badge>
                              </TableCell>
                              <TableCell className="text-right">{res.required_qty || 0}</TableCell>
                              <TableCell className="text-right">{res.issued_qty || 0}</TableCell>
                              <TableCell className="text-right font-bold text-orange-600">{res.pending_qty || 0}</TableCell>
                              <TableCell>{formatDate(res.delivery_date)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-muted/30 font-bold">
                            <TableCell colSpan={6}>Total Blocked</TableCell>
                            <TableCell className="text-right text-orange-600">
                              {nettStatusData.blocked_details.reduce((sum, r) => sum + (r.pending_qty || 0), 0)}
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">No stock reservations found</p>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </>
        )}
          </>
        )}

        {/* Detail Popup Dialog - Only shown in detail view */}
        {viewMode === 'detail' && (
          <Dialog open={!!detailPopup} onOpenChange={(open) => !open && setDetailPopup(null)}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detailPopup?.title}
                </DialogTitle>
                <DialogDescription>
                  {detailPopup?.description}
                </DialogDescription>
              </DialogHeader>
              
              {detailPopup && nettStatusData && (
                <div className="mt-4">
                  {/* Stock in Hand Details */}
                  {detailPopup.type === 'stock_in_hand' && (
                    <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-muted-foreground">Total Stock</p>
                        <p className="text-2xl font-bold" data-testid="popup-sih-primary">{(displaySih || 0).toFixed(2)}</p>
                        {sihDrift && (
                          <div className="mt-2 pt-2 border-t border-dashed border-blue-200" data-testid="popup-sih-legacy-block">
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <span>Legacy SIH (pre-rewrite)</span>
                              <span
                                title={LEGACY_SIH_TOOLTIP}
                                className="cursor-help inline-flex"
                                data-testid="popup-sih-legacy-tooltip"
                              >
                                <Info className="w-3 h-3" />
                              </span>
                            </div>
                            <p className="text-sm font-medium text-muted-foreground" data-testid="popup-sih-legacy-value">{cachedSih.toFixed(2)}</p>
                            <p
                              className={`text-xs font-medium ${sihDelta > 0 ? 'text-red-600' : 'text-amber-600'}`}
                              data-testid="popup-sih-legacy-delta"
                            >
                              Delta: {sihDelta > 0 ? '+' : ''}{sihDelta.toFixed(2)}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-muted-foreground">Stock Value</p>
                        <p className="text-2xl font-bold">{formatCurrency(stock?.current?.stock_value)}</p>
                      </div>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Source</TableHead>
                          <TableHead className="text-right">Quantity</TableHead>
                          <TableHead className="text-right">Value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell>Opening Stock</TableCell>
                          <TableCell className="text-right">{stock?.opening_stock?.quantity || 0}</TableCell>
                          <TableCell className="text-right">{formatCurrency(stock?.opening_stock?.value)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Purchase</TableCell>
                          <TableCell className="text-right">{stock?.incoming?.purchase?.quantity || 0}</TableCell>
                          <TableCell className="text-right">{formatCurrency(stock?.incoming?.purchase?.value)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Production</TableCell>
                          <TableCell className="text-right">{stock?.incoming?.production?.quantity || 0}</TableCell>
                          <TableCell className="text-right">{formatCurrency(stock?.incoming?.production?.value)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>BPD</TableCell>
                          <TableCell className="text-right">{stock?.incoming?.bpd?.quantity || 0}</TableCell>
                          <TableCell className="text-right">{formatCurrency(stock?.incoming?.bpd?.value)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>DC Inward</TableCell>
                          <TableCell className="text-right">{(stock?.incoming?.dc_inward?.quantity || 0).toFixed(2)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(stock?.incoming?.dc_inward?.value)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Stock Corrections</TableCell>
                          <TableCell className="text-right">{(stock?.incoming?.stock_corrections?.quantity || 0).toFixed(2)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(stock?.incoming?.stock_corrections?.value)}</TableCell>
                        </TableRow>
                        <TableRow className="border-t-2 font-bold">
                          <TableCell>Less: Outgoing</TableCell>
                          <TableCell className="text-right text-red-600">-{(stock?.outgoing?.total?.quantity || 0).toFixed(2)}</TableCell>
                          <TableCell className="text-right text-red-600">-{formatCurrency(stock?.outgoing?.total?.value)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* OHWIP - Work Orders created but not issued to production */}
                {detailPopup.type === 'ohwip' && (
                  <div>
                    {nettStatusData.ohwip_details?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>WO Number</TableHead>
                            <TableHead>SO Number</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">WO Qty</TableHead>
                            <TableHead className="text-right">Min Issued</TableHead>
                            <TableHead className="text-right">OHWIP</TableHead>
                            <TableHead>Created</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nettStatusData.ohwip_details.map((wo, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium font-mono">{wo.work_order_number || '-'}</TableCell>
                              <TableCell className="font-mono text-muted-foreground">{wo.so_number || '-'}</TableCell>
                              <TableCell>
                                <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">{wo.status}</Badge>
                              </TableCell>
                              <TableCell className="text-right">{(wo.quantity || 0).toFixed(0)}</TableCell>
                              <TableCell className="text-right">{(wo.min_issued || 0).toFixed(2)}</TableCell>
                              <TableCell className="text-right font-bold text-yellow-600">{(wo.ohwip_contribution || 0).toFixed(2)}</TableCell>
                              <TableCell>{formatDate(wo.created_at)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">No Work Orders pending production issue</p>
                    )}
                  </div>
                )}

                {/* WIP (Comp) - WOs issued to production */}
                {detailPopup.type === 'wip' && (
                  <div>
                    {nettStatusData.wip_comp_details?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>WO Number</TableHead>
                            <TableHead>SO Number</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">WO Qty</TableHead>
                            <TableHead className="text-right">WIP</TableHead>
                            <TableHead>Created</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nettStatusData.wip_comp_details.map((wo, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium font-mono">{wo.work_order_number || '-'}</TableCell>
                              <TableCell className="font-mono text-muted-foreground">{wo.so_number || '-'}</TableCell>
                              <TableCell>
                                <Badge variant="secondary" className="bg-purple-100 text-purple-800">{wo.status}</Badge>
                              </TableCell>
                              <TableCell className="text-right">{(wo.quantity || 0).toFixed(0)}</TableCell>
                              <TableCell className="text-right font-bold text-purple-600">{(wo.wip_contribution || 0).toFixed(2)}</TableCell>
                              <TableCell>{formatDate(wo.created_at)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">No Work Orders currently in production</p>
                    )}
                  </div>
                )}

                {/* WIP RMT - Raw materials issued to JCs/OJCs */}
                {detailPopup.type === 'wip_rmt' && (
                  <div>
                    {nettStatusData.wip_rmt_details?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Type</TableHead>
                            <TableHead>JC/OJC Number</TableHead>
                            <TableHead>WO Number</TableHead>
                            <TableHead>Jobber</TableHead>
                            <TableHead className="text-right">Quantity</TableHead>
                            <TableHead className="text-right">Unit Price</TableHead>
                            <TableHead className="text-right">Value</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nettStatusData.wip_rmt_details.map((entry, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                <Badge variant={entry.type === 'OJC' ? 'secondary' : 'outline'}>{entry.type || 'JC'}</Badge>
                              </TableCell>
                              <TableCell className="font-medium font-mono">{entry.jc_number || '-'}</TableCell>
                              <TableCell className="font-mono text-muted-foreground">{entry.work_order_number || '-'}</TableCell>
                              <TableCell>{entry.jobber_name || '-'}</TableCell>
                              <TableCell className="text-right font-bold text-orange-600">{(entry.quantity || 0).toFixed(2)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.unit_price)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.value)}</TableCell>
                              <TableCell>{formatDate(entry.date || entry.created_at)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">No raw materials issued to production</p>
                    )}
                  </div>
                )}

                {/* PO Issued */}
                {detailPopup.type === 'po_issued' && (
                  <div>
                    {nettStatusData.po_pending?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>PO Number</TableHead>
                            <TableHead>Supplier</TableHead>
                            <TableHead className="text-right">Qty Ordered</TableHead>
                            <TableHead className="text-right">Unit Price</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nettStatusData.po_pending.map((po, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium font-mono">{po.po_number || '-'}</TableCell>
                              <TableCell>{po.supplier_name || '-'}</TableCell>
                              <TableCell className="text-right">{po.quantity || 0}</TableCell>
                              <TableCell className="text-right">{formatCurrency(po.unit_price)}</TableCell>
                              <TableCell>
                                <Badge variant="secondary">{po.status}</Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">No pending Purchase Orders</p>
                    )}
                  </div>
                )}

                {/* Indent Qty - Pending Indents */}
                {detailPopup.type === 'indent_qty' && (
                  <div>
                    {nettStatusData.indent_pending?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Indent Number</TableHead>
                            <TableHead>Requested By</TableHead>
                            <TableHead className="text-right">Requested</TableHead>
                            <TableHead className="text-right">Ordered</TableHead>
                            <TableHead className="text-right">Pending</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Created</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nettStatusData.indent_pending.map((indent, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium font-mono">{indent.indent_number || '-'}</TableCell>
                              <TableCell>{indent.requested_by || '-'}</TableCell>
                              <TableCell className="text-right">{(indent.requested_qty || 0).toFixed(2)}</TableCell>
                              <TableCell className="text-right">{(indent.issued_qty || 0).toFixed(2)}</TableCell>
                              <TableCell className="text-right font-medium text-teal-600">{(indent.pending_qty || 0).toFixed(2)}</TableCell>
                              <TableCell>
                                <Badge variant="secondary">{indent.status}</Badge>
                              </TableCell>
                              <TableCell>{formatDate(indent.created_at)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">No pending Indents</p>
                    )}
                  </div>
                )}

                {/* Blocked for WO */}
                {detailPopup.type === 'blocked' && (
                  <div>
                    {nettStatusData.blocked_details?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>WO Number</TableHead>
                            <TableHead>FG Item</TableHead>
                            <TableHead>SO</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Required</TableHead>
                            <TableHead className="text-right">Issued</TableHead>
                            <TableHead className="text-right">Pending</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nettStatusData.blocked_details.map((res, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium font-mono">{res.work_order_number || '-'}</TableCell>
                              <TableCell className="text-sm text-muted-foreground truncate max-w-[200px]">{res.parent_item_name || '-'}</TableCell>
                              <TableCell className="font-mono text-sm">{res.so_number || '-'}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{res.wo_status || '-'}</Badge>
                              </TableCell>
                              <TableCell className="text-right">{(res.required_qty || 0).toFixed(2)}</TableCell>
                              <TableCell className="text-right">{(res.issued_qty || 0).toFixed(2)}</TableCell>
                              <TableCell className="text-right font-bold text-orange-600">{(res.pending_qty || 0).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="border-t-2 font-bold bg-orange-50">
                            <TableCell colSpan={6}>Total Blocked</TableCell>
                            <TableCell className="text-right text-orange-700">
                              {nettStatusData.blocked_details.reduce((sum, r) => sum + (r.pending_qty || 0), 0).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">No stock reservations found</p>
                    )}
                  </div>
                )}

                {/* SO Blocked */}
                {detailPopup.type === 'so_blocked' && (
                  <div>
                    {nettStatusData.so_blocked_details?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>SO Number</TableHead>
                            <TableHead>Client</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Qty</TableHead>
                            <TableHead className="text-right">Dispatched</TableHead>
                            <TableHead className="text-right">Pending</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nettStatusData.so_blocked_details.map((so, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium font-mono">{so.so_number || '-'}</TableCell>
                              <TableCell>{so.client_name || '-'}</TableCell>
                              <TableCell>
                                <Badge variant="secondary">{so.status}</Badge>
                              </TableCell>
                              <TableCell className="text-right">{so.quantity || 0}</TableCell>
                              <TableCell className="text-right">{so.dispatched_quantity || 0}</TableCell>
                              <TableCell className="text-right font-bold text-red-600">{so.pending_quantity || 0}</TableCell>
                              <TableCell>{formatDate(so.created_at)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">
                        SO Blocked quantity: {stock?.current?.so_blocked || 0}
                        <br />
                        <span className="text-sm">No detailed SO records found</span>
                      </p>
                    )}
                  </div>
                )}

                {/* Purchase Entries */}
                {detailPopup.type === 'purchase' && (
                  <div>
                    <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                      <strong>Note:</strong> Prices shown for older entries may include GST. Newer entries show base price excluding GST.
                    </div>
                    {nettStatusData.purchase_entries?.filter(e => e.reference_type === 'purchase_batch')?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>PE Reference</TableHead>
                            <TableHead className="text-right">Quantity</TableHead>
                            <TableHead className="text-right">Unit Price</TableHead>
                            <TableHead className="text-right">Value</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nettStatusData.purchase_entries?.filter(e => e.reference_type === 'purchase_batch').map((entry, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium font-mono">{entry.reference || '-'}</TableCell>
                              <TableCell className="text-right text-green-600">+{entry.quantity || 0}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.unit_price)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.value)}</TableCell>
                              <TableCell>{formatDate(entry.date)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">No purchase entries found</p>
                    )}
                  </div>
                )}

                {/* Production Entries */}
                {detailPopup.type === 'production' && (
                  <div>
                    {nettStatusData.production_batches?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>WO Reference</TableHead>
                            <TableHead className="text-right">Quantity</TableHead>
                            <TableHead className="text-right">Unit Price</TableHead>
                            <TableHead className="text-right">Value</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nettStatusData.production_batches.map((entry, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium font-mono">{entry.reference || '-'}</TableCell>
                              <TableCell className="text-right text-green-600">+{entry.quantity || 0}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.unit_price)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.value)}</TableCell>
                              <TableCell>{formatDate(entry.date)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">No production entries found</p>
                    )}
                  </div>
                )}

                {/* BPD Entries */}
                {detailPopup.type === 'bpd' && (
                  <div>
                    {nettStatusData.bpd_batches?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Reference</TableHead>
                            <TableHead className="text-right">Quantity</TableHead>
                            <TableHead className="text-right">Unit Price</TableHead>
                            <TableHead className="text-right">Value</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nettStatusData.bpd_batches.map((entry, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium font-mono">{entry.reference || '-'}</TableCell>
                              <TableCell className="text-right text-green-600">+{entry.quantity || 0}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.unit_price)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.value)}</TableCell>
                              <TableCell>{formatDate(entry.date)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">No BPD entries found</p>
                    )}
                  </div>
                )}

                {/* DC Inward */}
                {detailPopup.type === 'dc_inward' && (
                  <div>
                    {nettStatusData.dc_in_batches?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>DC Reference</TableHead>
                            <TableHead className="text-right">Quantity</TableHead>
                            <TableHead className="text-right">Unit Price</TableHead>
                            <TableHead className="text-right">Value</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nettStatusData.dc_in_batches.map((entry, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium font-mono">{entry.reference || '-'}</TableCell>
                              <TableCell className="text-right text-green-600">+{entry.quantity || 0}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.unit_price)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.value)}</TableCell>
                              <TableCell>{formatDate(entry.date)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">No DC Inward entries found</p>
                    )}
                  </div>
                )}

                {/* Stock Corrections — movements not backed by a batch (positive AND negative) */}
                {detailPopup.type === 'stock_corrections' && (
                  <div data-testid="popup-stock-corrections">
                    {nettStatusData.incoming?.stock_corrections?.by_type && Object.keys(nettStatusData.incoming.stock_corrections.by_type).length > 0 && (
                      <div className="mb-3 p-3 bg-green-50 rounded text-sm">
                        <div className="font-medium mb-1">By movement_type (signed):</div>
                        {Object.entries(nettStatusData.incoming.stock_corrections.by_type).map(([t, q]) => {
                          const qv = q || 0;
                          return (
                            <div key={t} className="flex justify-between">
                              <span>{t}</span>
                              <span className={qv >= 0 ? 'text-green-700' : 'text-red-700'}>{qv >= 0 ? '+' : ''}{qv.toFixed(2)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {nettStatusData.stock_correction_entries?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Movement Type</TableHead>
                            <TableHead>Reference</TableHead>
                            <TableHead className="text-right">Quantity</TableHead>
                            <TableHead>Remarks</TableHead>
                            <TableHead>Created By</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nettStatusData.stock_correction_entries.map((entry, idx) => {
                            const qv = entry.quantity || 0;
                            return (
                              <TableRow key={entry.id || idx} data-testid={`stock-correction-row-${idx}`}>
                                <TableCell className="font-medium">{entry.movement_type}</TableCell>
                                <TableCell className="font-mono text-xs">{entry.reference_number || entry.reference_id || '-'}</TableCell>
                                <TableCell className={`text-right ${qv >= 0 ? 'text-green-600' : 'text-red-600'}`}>{qv >= 0 ? '+' : ''}{qv.toFixed(2)}</TableCell>
                                <TableCell className="text-xs">{entry.remarks || '-'}</TableCell>
                                <TableCell className="text-xs">{entry.created_by || '-'}</TableCell>
                                <TableCell>{formatDate(entry.created_at)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">No stock correction entries</p>
                    )}
                  </div>
                )}

                {/* JC Issued (combined JC + OJC — Fix 7G rev2) */}
                {detailPopup.type === 'jc_issued' && (
                  <div>
                    {nettStatusData.jc_ojc_entries?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Type</TableHead>
                            <TableHead>WO Number</TableHead>
                            <TableHead>JC/OJC Number</TableHead>
                            <TableHead>Jobber</TableHead>
                            <TableHead>Item Code</TableHead>
                            <TableHead className="text-right">Quantity</TableHead>
                            <TableHead className="text-right">Unit Price</TableHead>
                            <TableHead className="text-right">Value</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nettStatusData.jc_ojc_entries.map((entry, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                <Badge variant={entry.type === 'OJC' ? 'secondary' : 'outline'}>{entry.type || 'JC'}</Badge>
                              </TableCell>
                              <TableCell className="font-medium font-mono">{entry.work_order_number || '-'}</TableCell>
                              <TableCell className="font-mono">{entry.jc_number || entry.reference || '-'}</TableCell>
                              <TableCell>{entry.jobber_name || '-'}</TableCell>
                              <TableCell className="font-mono">{entry.item_code || '-'}</TableCell>
                              <TableCell className="text-right text-red-600">-{entry.quantity || 0}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.unit_price)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.value)}</TableCell>
                              <TableCell>{formatDate(entry.date)}</TableCell>
                              <TableCell>{entry.status || entry.job_status || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">No JC/OJC issues found</p>
                    )}
                  </div>
                )}

                {/* Sales */}
                {detailPopup.type === 'sales' && (
                  <div>
                    {nettStatusData.sales_entries?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Reference</TableHead>
                            <TableHead className="text-right">Quantity</TableHead>
                            <TableHead className="text-right">Unit Price</TableHead>
                            <TableHead className="text-right">Value</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nettStatusData.sales_entries.map((entry, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium font-mono">{entry.reference || '-'}</TableCell>
                              <TableCell className="text-right text-red-600">-{entry.quantity || 0}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.unit_price)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.value)}</TableCell>
                              <TableCell>{formatDate(entry.date)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">No sales entries found</p>
                    )}
                  </div>
                )}

                {/* DC Outward */}
                {detailPopup.type === 'dc_outward' && (
                  <div>
                    <p className="text-center py-8 text-muted-foreground">
                      DC Outward: {stock?.outgoing?.dc_outward?.quantity || 0}
                    </p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
        )}
      </div>
    </DashboardLayout>
  );
}
